<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useLibraryStore } from './library.store'
import { useUserStore } from '@/authentication/user.store'
import { PII_DELETED_SENTINEL, DELETED_USER_NAME } from '@/authentication/user.utils'
import GbButton from '@/components/GbButton.vue'
import ProgressSpinner from 'primevue/progressspinner'
import FolderAvatar from './FolderAvatar.vue'
import PopupMenu from '@/components/PopupMenu.vue'
import { FolderComment } from './library_types'
import { timeAgo } from '@/util/time'
import type { MenuItemType } from '@/components/dropdown-menu/types'
import { ToastProvider, ToastRoot, ToastViewport } from 'reka-ui'
import ConfirmDialog from 'primevue/confirmdialog'
import { useConfirm } from 'primevue/useconfirm'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { useLibraryFolderAnalytics } from '@/library/composables/useLibraryFolderAnalytics'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { announce } from '@/util/live_announcer'

const props = defineProps({
  folderUuid: {
    type: String,
    required: true
  }
})

const libraryStore = useLibraryStore()
const userStore = useUserStore()
const { getReactiveEventAttributes } = useLibraryFolderAnalytics()
const analytics = getAnalyticsTracker().getChildContext(getReactiveEventAttributes())

const { currentFolderComments, currentFolderPermissions, currentFolder } = storeToRefs(libraryStore)
const { userId, getFolderMemberInfo } = storeToRefs(userStore)

const commentText = ref('')
const textareaElement = ref<HTMLTextAreaElement | null>(null)
const isSubmitting = ref(false)
const isLoadingComments = ref(false)
const confirm = useConfirm()
const loadError = ref(false)
const postError = ref(false)
const showToast = ref(false)
const toastMessage = ref('')
const MAX_COMMENT_LENGTH = 1000

const getCommentAuthor = (comment: FolderComment) => {
  return getFolderMemberInfo.value(currentFolder.value)?.[comment.user_uuid]
}

// Anyone can type the sentinel, so the comment only counts as redacted when its author is no longer an active member too
const isAuthorRemoved = (comment: FolderComment): boolean => {
  if (comment.content !== PII_DELETED_SENTINEL) {
    return false
  }
  const author = getCommentAuthor(comment)
  return !author || author.isDeleted
}

const canPostComments = computed(() => {
  return currentFolderPermissions.value.includes('add_content')
})

const isCommentTooLong = computed(() => {
  return commentText.value.length > MAX_COMMENT_LENGTH
})

const adjustTextareaHeight = () => {
  if (!textareaElement.value) return
  textareaElement.value.style.height = 'auto'
  textareaElement.value.style.height = `${textareaElement.value.scrollHeight}px`
}

const getCommentAuthorName = (comment: FolderComment): string => {
  return getCommentAuthor(comment)?.name || 'Unknown User'
}

const canDeleteComment = (comment: FolderComment): boolean => {
  return comment.user_uuid === userId.value
}

const getCommentMenuItems = (comment: FolderComment): MenuItemType[] => {
  const items: MenuItemType[] = []
  if (canDeleteComment(comment)) {
    items.push({
      label: 'Delete',
      action: () => handleDeleteClick(comment)
    })
  }
  return items
}

const handleAddComment = async () => {
  if (!commentText.value.trim()) return

  isSubmitting.value = true
  postError.value = false
  announce('Submitting comment...')
  const textToPost = commentText.value
  try {
    const response = await libraryStore.addCurrentFolderComment(props.folderUuid, textToPost)
    commentText.value = ''
    try {
      analytics.sendEvent(EVENT_NAMES.LIBRARY.FOLDER_COMMENT.POSTED_FOLDER_COMMENT, {
        comment_uuid: response.uuid,
        comment_character_count: textToPost.length,
        comment_text: textToPost,
        comment_count: currentFolderComments.value.length
      })
    } catch (analyticsError) {
      console.error('Failed to send Posted Folder Comment analytics event:', analyticsError)
    }
  } catch (error) {
    console.error('Failed to add comment:', error)
    postError.value = true
  } finally {
    isSubmitting.value = false
  }
}

const handleDeleteClick = (comment: FolderComment) => {
  confirm.require({
    group: `delete-comment-${comment.uuid}`,
    header: 'Delete your comment?',
    message: 'This will permanently remove your comment from this folder. This action cannot be undone.',
    accept: async () => {
      try {
        await libraryStore.deleteCurrentFolderComment(props.folderUuid, comment.uuid)
        try {
          analytics.sendEvent(EVENT_NAMES.LIBRARY.FOLDER_COMMENT.DELETED_FOLDER_COMMENT, {
            comment_uuid: comment.uuid,
            comment_count: currentFolderComments.value.length
          })
        } catch (analyticsError) {
          console.error('Failed to send Deleted Folder Comment analytics event:', analyticsError)
        }
      } catch (error: any) {
        console.error('Failed to delete comment:', error)
        if (error?.response?.status === 404) {
          toastMessage.value = 'This comment is no longer available'
          showToast.value = true
          await fetchComments()
        }
      }
    }
  })
}

const fetchComments = async () => {
  isLoadingComments.value = true
  loadError.value = false
  announce('Loading comments...')
  try {
    await libraryStore.fetchCurrentFolderComments(props.folderUuid)
  } catch (error) {
    console.error('Failed to fetch comments:', error)
    loadError.value = true
  } finally {
    isLoadingComments.value = false
  }
}

onMounted(async () => {
  if (currentFolderComments.value.length === 0) {
    await fetchComments()
  }
})

watch(() => props.folderUuid, () => {
  fetchComments()
})
</script>

<template>
  <div class="flex flex-col h-full bg-white rounded-lg border border-gray-400 overflow-hidden" style="width: 17.5rem;">
    <!-- Header -->
    <div class="flex items-center gap-2 px-4 py-3 bg-blue-400">
      <h2 class="m-0 text-white font-semibold text-base">Comments</h2>
      <span class="text-white text-sm font-medium">{{ currentFolderComments.length }}</span>
    </div>

    <!-- Comments List -->
    <div class="flex-1 overflow-y-auto px-4 py-4">
      <!-- Loading State -->
      <div v-if="isLoadingComments" class="flex items-center justify-center py-8" role="status" aria-live="polite">
        <ProgressSpinner aria-label="Loading comments" strokeWidth="8" class="h-5 w-5 stroke-primary-400" />
        <span class="ml-2 text-sm text-gray-700">Loading comments...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="loadError" class="flex flex-col items-center justify-center py-8 text-center gap-4">
        <p class="text-sm text-gray-900">Couldn't load comments.</p>
        <GbButton
          label="Reload"
          gbTheme="bluePrimary"
          size="small"
          @click="fetchComments"
        />
      </div>

      <!-- Empty State -->
      <div v-else-if="currentFolderComments.length === 0" class="flex flex-col items-center justify-center py-8 text-center">
        <h3 class="text-base font-semibold text-gray-900 mb-3">No comments yet</h3>
        <p class="text-sm text-gray-700 max-w-xs">Use comments to share context, ask questions, or coordinate with the folder team. Start the conversation below.</p>
      </div>

      <!-- Comments -->
      <div v-else class="space-y-4">
        <div v-for="(comment, index) in currentFolderComments" :key="comment.uuid" class="flex gap-3 pb-4 border-b border-gray-400 last:border-b-0">
          <!-- Avatar -->
          <div class="flex-shrink-0">
            <FolderAvatar
              :user-id="comment.user_uuid"
              :folder="libraryStore.currentFolder"
              avatar-size="sm"
              :deleted="isAuthorRemoved(comment)"
              :disable-tooltip="isAuthorRemoved(comment)"
              :right-margin="false"
            />
          </div>

          <!-- Comment Content -->
          <div class="flex-1 min-w-0">
            <!-- Header -->
            <div class="flex items-start justify-between gap-2">
              <div class="flex flex-col">
                <span v-if="isAuthorRemoved(comment)" class="text-sm text-gray-700">{{ DELETED_USER_NAME }}</span>
                <span v-else class="text-sm font-semibold text-gray-900 fs-exclude">{{ getCommentAuthorName(comment) }}</span>
                <span class="text-sm text-gray-700 fs-exclude">{{ timeAgo(comment.created_at) }}</span>
              </div>

              <!-- Actions Menu -->
              <div v-if="getCommentMenuItems(comment).length > 0" class="flex-shrink-0">
                <PopupMenu
                  :items="getCommentMenuItems(comment)"
                  :triggerAriaLabel="`Comment actions (comment ${index + 1} of ${currentFolderComments.length})`"
                  align="end"
                />
              </div>
            </div>

            <!-- Comment Text -->
            <p v-if="isAuthorRemoved(comment)" class="text-sm text-gray-700 mt-2">[Comment removed - account inactive]</p>
            <p v-else class="text-sm text-gray-700 mt-2 whitespace-pre-wrap break-words fs-exclude">{{ comment.content }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Comment Input -->
    <div v-if="canPostComments" class="border-t border-gray-400 p-4">
      <textarea
        ref="textareaElement"
        v-model="commentText"
        aria-label="Write a comment"
        placeholder="Write a comment..."
        :class="[
          'w-full px-3 py-2 text-sm rounded-md placeholder-gray-700 focus:outline-none focus:ring-2 focus:border-transparent resize-none overflow-hidden',
          isCommentTooLong || postError ? 'border border-red-300 focus:ring-red-300' : 'border border-gray-400 focus:ring-primary-400'
        ]"
        :disabled="isSubmitting || loadError"
        @input="adjustTextareaHeight"
      />
      <div class="flex justify-end items-center gap-2 mt-2">
        <div v-if="isSubmitting">
          <ProgressSpinner aria-label="Submitting comment" strokeWidth="8" class="h-4 w-4 stroke-primary-400" />
        </div>
        <GbButton
          label="Comment"
          gbTheme="blueFillSecondary"
          size="small"
          :disabled="!commentText.trim() || isSubmitting || loadError || isCommentTooLong"
          @click="handleAddComment"
        />
      </div>

      <!-- Character Limit Error -->
      <div v-if="isCommentTooLong" class="mt-2">
        <p class="text-sm text-red-300">Your comment is too long. Please shorten and resubmit.</p>
      </div>

      <!-- Post Error -->
      <div v-if="postError" class="mt-2">
        <p class="text-sm text-red-300">Couldn't post comment. Please try again.</p>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <ConfirmDialog
      v-for="comment in currentFolderComments"
      :key="comment.uuid"
      :group="`delete-comment-${comment.uuid}`"
      class="w-auto md:w-[38rem]"
    >
      <template #container="{ message, acceptCallback, rejectCallback }">
        <div class="flex flex-col rounded-md bg-surface-50">
          <div class="mt-2 flex justify-between border-b border-gray-300 p-3">
            <span class="block pl-3 text-xl font-medium">{{ message.header }}</span>
            <GbButton
              size="small"
              rounded
              :iconOnly="true"
              aria-label="close dialog"
              gbTheme="grayTertiary"
              class="mr-4 self-start"
              @click="rejectCallback"
            >
              <span class="material-icons-round">close</span>
            </GbButton>
          </div>
          <p class="py-5 pl-7 pr-12">{{ message.message }}</p>
          <div class="flex items-center justify-end gap-2 border-t border-gray-300 p-4">
            <GbButton label="Cancel" gbTheme="grayOutlineSecondary" @click="rejectCallback" />
            <GbButton label="Delete comment" gbTheme="redPrimary" @click="acceptCallback" />
          </div>
        </div>
      </template>
    </ConfirmDialog>

    <!-- Toast Notification -->
    <ToastProvider>
      <ToastRoot
        :open="showToast"
        @update:open="showToast = $event"
        class="rounded-md shadow-sm py-2 px-4 items-center data-[state=open]:animate-slideIn data-[state=closed]:animate-hide data-[swipe=cancel]:translate-x-0 data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=end]:animate-swipeOut data-[swipe=cancel]:transition-[transform_200ms_ease-out] bg-black text-white"
      >
        <div class="flex items-center">
          <span class="material-icons-outlined text-display-sm">info</span>
          <span class="ml-2">{{ toastMessage }}</span>
        </div>
      </ToastRoot>
      <ToastViewport
        v-if="showToast"
        :label="`Notification - comments`"
        class="fixed top-0 left-0 right-0 flex justify-center pt-[70px] px-[25px] gap-2.5 w-full max-w-[100vw] m-0 list-none z-[999999] outline-none"
      />
    </ToastProvider>
  </div>
</template>
